import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import api, { downloadFile, getMediaBlobUrl } from '../../utils/api';
import { 
  Image, Video, Download, Trash2, Upload, FolderPlus, Folder, ArrowLeft, 
  Eye, X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2, Edit, Play, Calendar, Images, Filter, Save, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useDropzone } from 'react-dropzone';
import BackButton from '../../components/BackButton';
import useAuthStore from '../../store/authStore';

// Year range from 2020 to current year
const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020];

// Colorful palette for albums
const ALBUM_COLORS = [
  { gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-500', light: 'bg-rose-100' },
  { gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-500', light: 'bg-violet-100' },
  { gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-500', light: 'bg-blue-100' },
  { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-500', light: 'bg-emerald-100' },
  { gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-500', light: 'bg-amber-100' },
  { gradient: 'from-cyan-500 to-sky-600', bg: 'bg-cyan-500', light: 'bg-cyan-100' },
];

const getAlbumColor = (index) => ALBUM_COLORS[index % ALBUM_COLORS.length];

const Gallery = () => {
  const { user } = useAuthStore();
  const [albums, setAlbums] = useState([]);
  const [currentAlbum, setCurrentAlbum] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [albumDialogOpen, setAlbumDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState('all');
  
  // Album cover URLs
  const [albumCovers, setAlbumCovers] = useState({});
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItem, setLightboxItem] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxLoading, setLightboxLoading] = useState(false);
  const [lightboxBlobUrl, setLightboxBlobUrl] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  
  // Thumbnail blob URLs cache
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  
  const [albumName, setAlbumName] = useState('');
  const [albumDescription, setAlbumDescription] = useState('');
  const [albumYear, setAlbumYear] = useState(2026);
  const [caption, setCaption] = useState('');
  const [files, setFiles] = useState([]);
  const [stagedFiles, setStagedFiles] = useState([]); // [{stagedFilename, originalFilename, type}]
  const [stagingInProgress, setStagingInProgress] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    setFiles(acceptedFiles);
    // Auto-stage files when dropped
    setStagingInProgress(true);
    const staged = [];
    
    for (const file of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/files/stage', formData);
        staged.push({
          stagedFilename: response.data.staged_filename,
          originalFilename: response.data.original_filename,
          fileSize: response.data.file_size,
          type: file.type.startsWith('video/') ? 'video' : 'image',
          localFile: file
        });
      } catch (error) {
        toast.error(`Failed to stage ${file.name}`);
      }
    }
    
    setStagedFiles(staged);
    setStagingInProgress(false);
    if (staged.length > 0) {
      toast.success(`${staged.length} file(s) staged. Click "Save All" to store permanently.`);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'video/*': ['.mp4', '.mov', '.avi', '.webm'],
    },
    maxFiles: 10,
    onDrop,
  });

  useEffect(() => { loadAlbums(); }, []);
  useEffect(() => { 
    if (currentAlbum) {
      loadItems(currentAlbum.id);
      setThumbnailUrls({});
    }
  }, [currentAlbum]);

  // Load thumbnails when items change
  useEffect(() => {
    const loadThumbnails = async () => {
      for (const item of items) {
        if (!thumbnailUrls[item.id] && item.type === 'image') {
          try {
            const blobUrl = await getMediaBlobUrl(item.file_url);
            if (blobUrl) {
              setThumbnailUrls(prev => ({ ...prev, [item.id]: blobUrl }));
            }
          } catch (error) {
            console.error('Failed to load thumbnail:', item.id);
          }
        }
      }
    };
    if (items.length > 0) {
      loadThumbnails();
    }
  }, [items]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(thumbnailUrls).forEach(url => {
        if (url) window.URL.revokeObjectURL(url);
      });
      if (lightboxBlobUrl) window.URL.revokeObjectURL(lightboxBlobUrl);
    };
  }, []);

  const loadAlbums = async () => {
    try {
      setLoading(true);
      const response = await api.get('/gallery/albums');
      setAlbums(response.data);
    } catch (error) {
      toast.error('Failed to load albums');
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async (albumId) => {
    try {
      setLoading(true);
      const response = await api.get(`/gallery/items/${albumId}`);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load gallery items');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/gallery/albums', { name: albumName, description: albumDescription, year: albumYear, created_by: user.user_id });
      toast.success('Album created successfully');
      setAlbumDialogOpen(false);
      setAlbumName('');
      setAlbumDescription('');
      setAlbumYear(2026);
      loadAlbums();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create album');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFiles = async (e) => {
    e.preventDefault();
    if (!currentAlbum || stagedFiles.length === 0) {
      toast.error('Please upload and stage files first');
      return;
    }
    setLoading(true);
    try {
      for (const staged of stagedFiles) {
        // Save each staged file permanently
        const saveResponse = await api.post(`/files/save?staged_filename=${staged.stagedFilename}`);
        
        // Create gallery item with permanent file URL
        await api.post('/gallery/items', {
          album_id: currentAlbum.id,
          file_url: saveResponse.data.file_url,
          type: staged.type,
          caption: caption || staged.originalFilename,
          uploaded_by: user.user_id,
        });
      }
      toast.success(`${stagedFiles.length} file(s) saved permanently!`);
      setUploadDialogOpen(false);
      setCaption('');
      setFiles([]);
      setStagedFiles([]);
      loadItems(currentAlbum.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save files');
    } finally {
      setLoading(false);
    }
  };

  const cancelStagedFiles = async () => {
    for (const staged of stagedFiles) {
      try {
        await api.delete(`/files/stage/${staged.stagedFilename}`);
      } catch (error) {
        console.error('Failed to cancel staged file:', staged.stagedFilename);
      }
    }
    setStagedFiles([]);
    setFiles([]);
  };

  const handleDeleteAlbum = async (albumId, albumName) => {
    if (!window.confirm(`Delete album "${albumName}" and all its contents?`)) return;
    try {
      await api.delete(`/gallery/albums/${albumId}`);
      toast.success('Album deleted');
      if (currentAlbum?.id === albumId) { setCurrentAlbum(null); setItems([]); }
      loadAlbums();
    } catch (error) {
      toast.error('Failed to delete album');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.delete(`/gallery/items/${itemId}`);
      toast.success('Item deleted');
      loadItems(currentAlbum.id);
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Open lightbox viewer
  const openLightbox = async (item, index) => {
    setLightboxItem(item);
    setLightboxIndex(index);
    setLightboxOpen(true);
    setLightboxLoading(true);
    setZoomLevel(1);
    
    try {
      const blobUrl = await getMediaBlobUrl(item.file_url);
      setLightboxBlobUrl(blobUrl);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLightboxLoading(false);
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxItem(null);
    if (lightboxBlobUrl) {
      window.URL.revokeObjectURL(lightboxBlobUrl);
      setLightboxBlobUrl(null);
    }
    setZoomLevel(1);
  };

  const navigateLightbox = async (direction) => {
    const newIndex = direction === 'next' 
      ? (lightboxIndex + 1) % items.length 
      : (lightboxIndex - 1 + items.length) % items.length;
    
    const newItem = items[newIndex];
    setLightboxIndex(newIndex);
    setLightboxItem(newItem);
    setLightboxLoading(true);
    setZoomLevel(1);
    
    if (lightboxBlobUrl) {
      window.URL.revokeObjectURL(lightboxBlobUrl);
    }
    
    try {
      const blobUrl = await getMediaBlobUrl(newItem.file_url);
      setLightboxBlobUrl(blobUrl);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLightboxLoading(false);
    }
  };

  const handleDownload = async (item) => {
    try {
      await downloadFile(item.file_url, item.caption);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed. Please try again.');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  // Album list view
  if (!currentAlbum) {
    return (
      <div className="space-y-6" data-testid="admin-gallery-page">
        <BackButton to="/admin" />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-serif font-bold text-white">Gallery Management</h1>
            <p className="text-slate-400">Create albums and manage photos & videos</p>
          </div>
          <Dialog open={albumDialogOpen} onOpenChange={setAlbumDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-600 hover:bg-orange-700" data-testid="create-album-btn">
                <FolderPlus className="w-4 h-4 mr-2" />Create Album
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 text-white border-slate-700">
              <DialogHeader><DialogTitle className="text-white">Create New Album</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateAlbum} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Album Name</Label>
                  <Input value={albumName} onChange={(e) => setAlbumName(e.target.value)} placeholder="e.g., Annual Meeting 2024" required className="bg-slate-700 border-slate-600 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Description (Optional)</Label>
                  <textarea value={albumDescription} onChange={(e) => setAlbumDescription(e.target.value)} placeholder="Brief description..." rows={3} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-rose-500" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Year</Label>
                  <Select value={albumYear.toString()} onValueChange={(v) => setAlbumYear(parseInt(v))}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      {YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex space-x-2 pt-4">
                  <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600">{loading ? 'Creating...' : 'Create Album'}</Button>
                  <Button type="button" variant="outline" onClick={() => setAlbumDialogOpen(false)} className="flex-1">Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Year Filter */}
        <div className="flex flex-wrap gap-2 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
          <span className="text-slate-400 text-sm self-center mr-2">Filter by Year:</span>
          <button onClick={() => setSelectedYear('all')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedYear === 'all' ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-lg' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>All Years</button>
          {YEARS.map(y => (
            <button key={y} onClick={() => setSelectedYear(y.toString())} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedYear === y.toString() ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-lg' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>{y}</button>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-rose-500/20 to-pink-500/20 border border-rose-500/30 rounded-xl p-4">
            <p className="text-rose-400 text-sm font-medium">Total Albums</p>
            <p className="text-2xl font-bold text-white">{albums.length}</p>
          </div>
          <div className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/30 rounded-xl p-4">
            <p className="text-violet-400 text-sm font-medium">This Year</p>
            <p className="text-2xl font-bold text-white">{albums.filter(a => new Date(a.created_at).getFullYear() === 2026).length}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-xl p-4">
            <p className="text-blue-400 text-sm font-medium">From 2020</p>
            <p className="text-2xl font-bold text-white">{[...new Set(albums.map(a => new Date(a.created_at).getFullYear()))].filter(y => y >= 2020).length}</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4">
            <p className="text-emerald-400 text-sm font-medium">View Mode</p>
            <p className="text-lg font-bold text-white">Professional</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
          </div>
        ) : albums.filter(a => selectedYear === 'all' || new Date(a.created_at).getFullYear().toString() === selectedYear).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {albums.filter(a => selectedYear === 'all' || new Date(a.created_at).getFullYear().toString() === selectedYear).map((album, index) => {
              const colors = getAlbumColor(index);
              const year = new Date(album.created_at).getFullYear();
              return (
                <div key={album.id} className="group bg-slate-800 rounded-2xl overflow-hidden border-2 border-slate-700 hover:border-rose-500/50 cursor-pointer transition-all duration-300 hover:shadow-xl hover:shadow-rose-500/10 hover:-translate-y-1">
                  {/* Cover Image / Color Block */}
                  <div className={`h-40 bg-gradient-to-br ${colors.gradient} relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-black/20" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Images className="w-16 h-16 text-white/50" />
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full font-medium">{year}</span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteAlbum(album.id, album.name); }} className="absolute top-3 right-3 p-2 bg-black/30 hover:bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all" title="Delete Album">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="p-4" onClick={() => setCurrentAlbum(album)}>
                    <h3 className="font-semibold text-lg text-white mb-1 group-hover:text-rose-400 transition-colors line-clamp-1">{album.name}</h3>
                    {album.description && <p className="text-sm text-slate-400 mb-2 line-clamp-2">{album.description}</p>}
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />{formatDate(album.created_at)}</span>
                      <span className="text-rose-400 group-hover:underline">Open Album →</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl text-center py-16">
            <Images className="w-16 h-16 mx-auto mb-4 text-slate-600" />
            <p className="text-slate-400 text-lg">{selectedYear === 'all' ? 'No albums yet' : `No albums for ${selectedYear}`}</p>
            <p className="text-sm text-slate-500 mt-2">Create your first album to get started</p>
          </div>
        )}
      </div>
    );
  }

  // Album items view with professional gallery
  return (
    <div className="space-y-6" data-testid="gallery-items-view">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={() => { setCurrentAlbum(null); setItems([]); setThumbnailUrls({}); }} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Albums
        </Button>
      </div>
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif font-bold text-white">{currentAlbum.name}</h1>
          <p className="text-slate-400">{currentAlbum.description || 'Manage photos and videos'}</p>
          <p className="text-xs text-slate-500 mt-1">{items.length} items</p>
        </div>
        <Dialog open={uploadDialogOpen} onOpenChange={(open) => { 
          if (!open && stagedFiles.length > 0) {
            cancelStagedFiles();
          }
          setUploadDialogOpen(open); 
        }}>
          <DialogTrigger asChild>
            <Button className="bg-orange-600 hover:bg-orange-700">
              <Upload className="w-4 h-4 mr-2" />Upload Media
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-slate-800 text-white border-slate-700">
            <DialogHeader><DialogTitle className="text-white">Upload to {currentAlbum.name}</DialogTitle></DialogHeader>
            <form onSubmit={handleUploadFiles} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Select Files (Two-Step: Upload → Save)</Label>
                <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  stagingInProgress ? 'border-blue-500 bg-blue-500/10' :
                  isDragActive ? 'border-orange-500 bg-orange-500/10' : 
                  'border-slate-600 hover:border-slate-500 bg-slate-700/50'
                }`}>
                  <input {...getInputProps()} disabled={stagingInProgress} />
                  {stagingInProgress ? (
                    <>
                      <Loader2 className="w-12 h-12 mx-auto mb-3 text-blue-500 animate-spin" />
                      <p className="text-blue-400">Staging files...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                      <p className="text-slate-300">Drag & drop files here</p>
                      <p className="text-xs text-slate-500 mt-1">Images or Videos (up to 10)</p>
                    </>
                  )}
                </div>
              </div>
              
              {/* Staged Files List */}
              {stagedFiles.length > 0 && (
                <div className="border-2 border-amber-500 bg-amber-500/10 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-amber-400 font-medium text-sm uppercase tracking-wider">
                      {stagedFiles.length} File(s) Staged
                    </p>
                    <span className="text-xs text-amber-300">Click "Save All" to store permanently</span>
                  </div>
                  <ul className="space-y-2 max-h-40 overflow-y-auto">
                    {stagedFiles.map((sf, i) => (
                      <li key={i} className="flex items-center justify-between bg-slate-700/50 rounded-lg px-3 py-2">
                        <div className="flex items-center space-x-2">
                          {sf.type === 'video' ? <Video className="w-4 h-4 text-violet-400" /> : <Image className="w-4 h-4 text-emerald-400" />}
                          <span className="text-sm text-slate-300">{sf.originalFilename}</span>
                        </div>
                        <Check className="w-4 h-4 text-amber-400" />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-slate-300">Caption (Optional)</Label>
                <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Add a caption..." className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  disabled={loading || stagedFiles.length === 0} 
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving...' : `Save All ${stagedFiles.length} File(s)`}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setUploadDialogOpen(false); cancelStagedFiles(); }} className="flex-1">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Professional Gallery Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : items.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {items.map((item, index) => (
            <div 
              key={item.id} 
              className="group relative bg-slate-800 rounded-xl overflow-hidden border border-slate-700 hover:border-orange-500 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/10 cursor-pointer"
              onClick={() => openLightbox(item, index)}
              onContextMenu={(e) => e.preventDefault()}
              data-testid={`gallery-item-${item.id}`}
            >
              {/* Thumbnail */}
              <div className="aspect-square relative overflow-hidden bg-slate-900">
                {item.type === 'video' ? (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                        <Play className="w-8 h-8 text-white" fill="white" />
                      </div>
                    </div>
                    <Video className="w-12 h-12 text-slate-600 absolute bottom-2 right-2" />
                  </div>
                ) : (
                  <>
                    {thumbnailUrls[item.id] ? (
                      <img 
                        src={thumbnailUrls[item.id]} 
                        alt={item.caption} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        draggable="false"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
                      </div>
                    )}
                  </>
                )}
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="flex items-center space-x-2">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 hover:bg-white/30 transition-colors">
                      <Eye className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
                
                {/* Media Type Badge */}
                <div className="absolute top-2 left-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${item.type === 'video' ? 'bg-purple-500/80 text-white' : 'bg-blue-500/80 text-white'}`}>
                    {item.type === 'video' ? 'Video' : 'Image'}
                  </span>
                </div>
              </div>
              
              {/* Item Info */}
              <div className="p-3">
                <p className="text-sm text-white truncate font-medium">{item.caption || 'Untitled'}</p>
                <p className="text-xs text-slate-500 mt-1">{formatDate(item.created_at)}</p>
              </div>
              
              {/* Delete Button (Admin) */}
              <button 
                onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }} 
                className="absolute top-2 right-2 p-2 bg-red-500/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                title="Delete"
              >
                <Trash2 className="w-4 h-4 text-white" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-xl text-center py-16">
          <Image className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 text-lg">No media in this album</p>
          <p className="text-sm text-slate-500 mt-2">Upload photos or videos to get started</p>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxOpen && lightboxItem && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-50"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          
          {/* Navigation Arrows */}
          {items.length > 1 && (
            <>
              <button 
                onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-50"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-50"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            </>
          )}
          
          {/* Media Content */}
          <div 
            className="max-w-[90vw] max-h-[80vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            {lightboxLoading ? (
              <div className="flex items-center justify-center w-64 h-64">
                <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
              </div>
            ) : lightboxBlobUrl ? (
              lightboxItem.type === 'video' ? (
                <video 
                  src={lightboxBlobUrl} 
                  controls 
                  autoPlay
                  className="max-w-full max-h-[80vh] rounded-lg"
                  onContextMenu={(e) => e.preventDefault()}
                />
              ) : (
                <img 
                  src={lightboxBlobUrl} 
                  alt={lightboxItem.caption}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg transition-transform duration-300"
                  style={{ transform: `scale(${zoomLevel})` }}
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                />
              )
            ) : (
              <div className="flex items-center justify-center w-64 h-64 bg-slate-800 rounded-lg">
                <p className="text-slate-400">Failed to load media</p>
              </div>
            )}
          </div>
          
          {/* Bottom Controls */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold text-lg">{lightboxItem.caption || 'Untitled'}</h3>
                  <p className="text-slate-400 text-sm">{lightboxIndex + 1} of {items.length} • {formatDate(lightboxItem.created_at)}</p>
                </div>
                <div className="flex items-center space-x-3">
                  {lightboxItem.type === 'image' && (
                    <>
                      <button 
                        onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.25))}
                        className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                        title="Zoom Out"
                      >
                        <ZoomOut className="w-5 h-5 text-white" />
                      </button>
                      <span className="text-white text-sm">{Math.round(zoomLevel * 100)}%</span>
                      <button 
                        onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))}
                        className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                        title="Zoom In"
                      >
                        <ZoomIn className="w-5 h-5 text-white" />
                      </button>
                    </>
                  )}
                  <Button 
                    onClick={() => handleDownload(lightboxItem)}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="lightbox-download-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
